// FerrumIT Voice Pricing Engine
// Handles Hosted Seats, Hybrid Hosting, and SIP Trunking quote types

import { supabase } from './supabase';

// ─── VOICE HARDWARE CATALOG (DB-backed) ──────────────────────────────────────
// Loads phones and ATAs from the voice_hardware table at runtime.
// Phones (Yealink etc.): rows where catalog_id IS NOT NULL AND
//                        (lease_eligible OR purchase_eligible) AND
//                        hardware_type IN ('phone','dect')
// ATAs:                  rows where catalog_id IS NOT NULL AND
//                        purchase_eligible AND hardware_type='ata'
//
// Field-name normalization (DB → consumer-shape):
//   catalog_id        → id
//   short_label       → label
//   short_description → desc
//   monthly_lease     → monthly      (for phones)
//   purchase_price    → nrc          (for phones)
//   monthly_lease     → monthly      (for ATAs — null in DB, defaults to 15.00 below)
//   purchase_price    → hardware_nrc (for ATAs)
//   ports             → ports
//
// On fetch failure or empty result: returns empty arrays + _fallback:true.
// Reps will see empty hardware dropdowns until the DB issue is resolved —
// strictly more honest than serving stale prices from old hardcoded constants
// (which is what the pre-v3.5.29 fallback did).
export async function loadVoiceHardwareCatalog() {
  try {
    const { data, error } = await supabase
      .from('voice_hardware')
      .select('catalog_id, short_label, short_description, hardware_type, monthly_lease, purchase_price, ports, lease_eligible, purchase_eligible, sort_order')
      .eq('active', true)
      .not('catalog_id', 'is', null)
      .order('sort_order', { ascending: true });

    if (error) throw error;
    if (!Array.isArray(data) || data.length === 0) {
      console.warn('voice_hardware catalog returned empty');
      return { yealink: [], atas: [], _fallback: true };
    }

    const yealink = [];
    const atas = [];
    for (const row of data) {
      const isAta = row.hardware_type === 'ata' || row.hardware_type === 'gateway';
      const obj = {
        id:    row.catalog_id,
        label: row.short_label || row.catalog_id,
        desc:  row.short_description || '',
        sort:  row.sort_order ?? 100,
      };

      if (isAta) {
        // ATA shape — older constants used hardware_nrc + flat $15/mo service fee.
        // We keep the $15 default for the per-port voice service fee (it's a service
        // charge, not a hardware lease — DB only tracks the hardware purchase price).
        atas.push({
          ...obj,
          hardware_nrc: row.purchase_price != null ? Number(row.purchase_price) : 0,
          monthly:      15.00,
          ports:        row.ports || 1,
        });
      } else if (row.lease_eligible || row.purchase_eligible) {
        yealink.push({
          ...obj,
          monthly: row.monthly_lease  != null ? Number(row.monthly_lease)  : null,
          nrc:     row.purchase_price != null ? Number(row.purchase_price) : null,
          // Carry eligibility through so dropdowns can hide non-eligible options
          lease_eligible:    !!row.lease_eligible,
          purchase_eligible: !!row.purchase_eligible,
        });
      }
    }

    yealink.sort((a, b) => a.sort - b.sort);
    atas.sort((a, b) => a.sort - b.sort);

    return { yealink, atas, _fallback: false };
  } catch (err) {
    console.error('loadVoiceHardwareCatalog failed:', err);
    return { yealink: [], atas: [], _fallback: true };
  }
}

// ─── 3CX LICENSE TIERS ───────────────────────────────────────────────────────
// v3.5.77: License tier catalog migrated from hardcoded constant to DB-backed
// (voice_cx_tiers table). loadVoiceCXTiers() is the runtime fetcher; mirror
// of loadVoiceHardwareCatalog above. Components load on mount and pass the
// result through to getCXTiers/getRecommendedTier as the second argument.
//
// Why migrate: each tier becomes a Schedule of Services line item, so each
// tier needs a stable DB row that can carry SoS narrative fields in v3.5.78.
// CX_TIERS_BASE is retained as a transient-error fallback ONLY — pricing
// flows through the DB rows when they're available.
//
// Field mapping:
//   id                    → id
//   label                 → label
//   paths                 → paths
//   ext_min               → ext_min
//   ext_max               → ext_max
//   annual_license_cost   → annual_cost      (renamed for clarity in DB)
//   type                  → type             ('pro' | 'enterprise')
//
// On fetch failure or empty result: returns [] which causes getCXTiers to
// fall back to CX_TIERS_BASE. Same safety pattern as the hardware migration.
export async function loadVoiceCXTiers() {
  try {
    const { data, error } = await supabase
      .from('voice_cx_tiers')
      .select('id, label, paths, ext_min, ext_max, annual_license_cost, type, sort_order')
      .eq('active', true)
      .order('sort_order', { ascending: true });

    if (error) throw error;
    if (!Array.isArray(data) || data.length === 0) {
      console.warn('voice_cx_tiers returned empty; falling back to CX_TIERS_BASE');
      return [];
    }

    return data.map(row => ({
      id:          row.id,
      label:       row.label,
      paths:       parseInt(row.paths, 10),
      ext_min:     parseInt(row.ext_min, 10),
      ext_max:     parseInt(row.ext_max, 10),
      annual_cost: parseFloat(row.annual_license_cost),
      type:        row.type,
    }));
  } catch (err) {
    console.error('loadVoiceCXTiers failed:', err);
    return [];
  }
}

// Base tiers — used as fallback only when the DB rows aren't available.
// Real values come from voice_cx_tiers table via loadVoiceCXTiers().
const CX_TIERS_BASE = [
  { id: 'pro_8',   label: '3CX Pro — 8 Paths',          paths: 8,  ext_min: 1,   ext_max: 40,  annual_cost: 395,  type: 'pro' },
  { id: 'pro_16',  label: '3CX Pro — 16 Paths',         paths: 16, ext_min: 41,  ext_max: 80,  annual_cost: 795,  type: 'pro' },
  { id: 'pro_24',  label: '3CX Pro — 24 Paths',         paths: 24, ext_min: 81,  ext_max: 120, annual_cost: 1095, type: 'pro' },
  { id: 'ent_8',   label: '3CX Enterprise — 8 Paths',   paths: 8,  ext_min: 1,   ext_max: 40,  annual_cost: 575,  type: 'enterprise' },
  { id: 'ent_16',  label: '3CX Enterprise — 16 Paths',  paths: 16, ext_min: 41,  ext_max: 80,  annual_cost: 1095, type: 'enterprise' },
  { id: 'ent_24',  label: '3CX Enterprise — 24 Paths',  paths: 24, ext_min: 81,  ext_max: 120, annual_cost: 1495, type: 'enterprise' },
];

// v3.5.77: getCXTiers now accepts optional dbTiers as second argument.
// When dbTiers is provided and non-empty, it IS the catalog — no settings
// override needed because admin edits go directly to the DB row's
// annual_license_cost column. When dbTiers is empty (transient error,
// not-yet-loaded, or pre-migration tenant), falls back to CX_TIERS_BASE
// with the legacy pricing_settings.cx_license_* override mechanism.
//
// External callers should always pass dbTiers; the optional signature is
// kept only so internal voicePricing.js callers (calcVoice, etc.) can
// receive the DB rows from the page-level fetch and forward them down.
export function getCXTiers(settings, dbTiers) {
  if (Array.isArray(dbTiers) && dbTiers.length > 0) {
    return dbTiers;
  }
  const s = settings || {};
  return CX_TIERS_BASE.map(t => ({
    ...t,
    annual_cost: parseFloat(s[`cx_license_${t.id}`] || t.annual_cost),
  }));
}

// Static export for components that don't have settings yet.
// v3.5.77: deprecated — kept for backwards compatibility during the migration.
// New code should use loadVoiceCXTiers() + getCXTiers(settings, dbTiers).
// Slated for removal in a future deploy once all callers verified migrated.
export const CX_TIERS = CX_TIERS_BASE;

// Lightsail instance tiers by seat count
// v3.5.86.8: seat-count thresholds (small_max, medium_max) are now
// settings-driven via voice_lightsail_small_max_seats /
// voice_lightsail_medium_max_seats. Defaults preserve prior behavior:
// 20 / 50. Dollar amounts have been settings-driven since v3.5.77.
// The v3.5.77 comment claiming the thresholds were "intentionally
// hardcoded" no longer applies — Shaun confirmed they should be editable
// (Phase 0 #4, May 9 2026 — closes the last item on the voice pricing
// flexibility punch list). SQL migration:
// 2026_05_09_lightsail_thresholds.sql.
//
// Helper to resolve tier metadata once per call. Returns { tier, cost,
// label, smallMax, medMax } — keeps getLightsailCost and
// getLightsailLabel in lockstep so the cost number and the label always
// agree on which tier was picked.
function getLightsailTier(seats, settings) {
  const s = settings || {};
  const smallMaxRaw = parseInt(s.voice_lightsail_small_max_seats, 10);
  const medMaxRaw   = parseInt(s.voice_lightsail_medium_max_seats, 10);
  const smallMax    = Number.isFinite(smallMaxRaw) ? smallMaxRaw : 20;
  const medMax      = Number.isFinite(medMaxRaw)   ? medMaxRaw   : 50;
  if (seats <= smallMax) {
    return { tier: 'small',  cost: parseFloat(s.voice_lightsail_small  || 24), label: `Lightsail Small (≤${smallMax} seats) — $${parseFloat(s.voice_lightsail_small||24)}/mo`,  smallMax, medMax };
  }
  if (seats <= medMax) {
    return { tier: 'medium', cost: parseFloat(s.voice_lightsail_medium || 48), label: `Lightsail Medium (≤${medMax} seats) — $${parseFloat(s.voice_lightsail_medium||48)}/mo`, smallMax, medMax };
  }
  return   { tier: 'large',  cost: parseFloat(s.voice_lightsail_large  || 96), label: `Lightsail Large (${medMax}+ seats) — $${parseFloat(s.voice_lightsail_large||96)}/mo`,    smallMax, medMax };
}

export function getLightsailCost(seats, settings) {
  return getLightsailTier(seats, settings).cost;
}

export function getLightsailLabel(seats, settings) {
  return getLightsailTier(seats, settings).label;
}

// v3.5.77: getRecommendedTier now accepts dbTiers as fourth argument and
// forwards it to getCXTiers. Existing callers without dbTiers continue to
// work via the CX_TIERS_BASE fallback.
export function getRecommendedTier(seats, licenseType, settings, dbTiers) {
  const tiers = getCXTiers(settings, dbTiers);
  return tiers.find(t => t.type === licenseType && seats <= t.ext_max) || tiers.filter(t => t.type === licenseType).slice(-1)[0];
}

// ─── FAX PACKAGES ────────────────────────────────────────────────────────────
// Fallback constant — used when DB rows aren't available (initial mount, error, etc.).
// Cost values default to 0 here so margins reflect "unknown cost" rather than guessed cost.
// Real values come from voice_fax_packages table via getFaxPackages(rows).
export const FAX_PACKAGES = {
  email_only: { label: 'Email-Only Fax',         price: 9.95,   cost: 0, users: 1,   pages: null, dids: 0, overage_page: null, overage_cost: 0, extra_did: null, extra_did_cost: 0, extra_user: null, extra_user_cost: 0, desc: 'Fax to email only — no portal, no DID' },
  solo:       { label: 'Virtual Fax — Solo',      price: 12.00,  cost: 0, users: 1,   pages: 50,   dids: 1, overage_page: 0.10, overage_cost: 0, extra_did: null, extra_did_cost: 0, extra_user: null, extra_user_cost: 0, desc: '1 user · 50 pages/mo · 1 DID · $0.10/page overage' },
  team:       { label: 'Virtual Fax — Team',      price: 29.00,  cost: 0, users: 5,   pages: 500,  dids: 1, overage_page: 0.08, overage_cost: 0, extra_did: null, extra_did_cost: 0, extra_user: null, extra_user_cost: 0, desc: '5 users · 500 pages/mo · 1 DID · $0.08/page overage' },
  business:   { label: 'Virtual Fax — Business',  price: 59.00,  cost: 0, users: 15,  pages: 1000, dids: 1, overage_page: 0.06, overage_cost: 0, extra_did: 3.00, extra_did_cost: 0, extra_user: 3.00, extra_user_cost: 0, desc: '15 users · 1,000 pages/mo · 1 DID + $3/extra DID or user' },
  infinity:   { label: 'Virtual Fax — Infinity',  price: 119.00, cost: 0, users: 50,  pages: 2500, dids: 1, overage_page: 0.05, overage_cost: 0, extra_did: 2.00, extra_did_cost: 0, extra_user: 2.00, extra_user_cost: 0, desc: '50 users · 2,500 pages/mo · 1 DID + $2/extra DID or user' },
};

// Convert voice_fax_packages DB rows into the same shape as the FAX_PACKAGES constant,
// keyed by package_key. Falls back to the constant for any package_key not in the rows.
export function getFaxPackages(dbRows) {
  if (!Array.isArray(dbRows) || dbRows.length === 0) return FAX_PACKAGES;
  const out = {};
  for (const r of dbRows) {
    if (!r.active) continue;
    out[r.package_key] = {
      label:           r.label,
      price:           parseFloat(r.sell_mrr || 0),
      cost:            parseFloat(r.cost_mrr || 0),
      users:           parseInt(r.included_users || 1),
      pages:           r.included_pages == null ? null : parseInt(r.included_pages),
      dids:            parseInt(r.included_dids || 0),
      overage_page:    r.overage_sell_per_page == null ? null : parseFloat(r.overage_sell_per_page),
      overage_cost:    parseFloat(r.overage_cost_per_page || 0),
      extra_user:      r.extra_user_sell == null ? null : parseFloat(r.extra_user_sell),
      extra_user_cost: parseFloat(r.extra_user_cost || 0),
      extra_did:       r.extra_did_sell  == null ? null : parseFloat(r.extra_did_sell),
      extra_did_cost:  parseFloat(r.extra_did_cost  || 0),
      desc:            r.description || '',
    };
  }
  return Object.keys(out).length > 0 ? out : FAX_PACKAGES;
}

// v3.5.86.6: refactored to read package capacity directly from the
// voice_fax_packages table (admin-editable via Admin → Voice Fax Packages).
// Prior to this refactor (v3.5.86.2.2 → v3.5.86.5) the function read
// thresholds from pricing_settings rows like voice_fax_solo_max_users —
// duplicating data that already lives on each package row's included_users
// / included_dids columns. Worse, the VoiceQuotePage call sites never
// actually passed `settings`, so those rows have been silently unused since
// they were created. SQL migration 2026_05_08_drop_legacy_fax_suggestion_settings.sql
// removes them.
//
// Now the function picks the smallest package whose capacity satisfies the
// requested users/DIDs. When admin adds a new package via the Voice Fax
// Packages admin page, the suggester picks it up automatically — no code
// change needed to introduce a new tier.
//
// `faxPackages` argument:
//   - Array of voice_fax_packages DB rows → normalized via getFaxPackages
//   - Object dict in FAX_PACKAGES shape → used directly
//   - null / undefined → falls back to FAX_PACKAGES constant
//
// `email_only` is intentionally excluded from suggestion (it's the special
// no-DID tier; reps select it explicitly when there's no inbound need).
// `none` is also excluded.
export function suggestFaxPackage(users, dids, faxPackages) {
  if (!users && !dids) return null;
  const u = parseInt(users) || 0;
  const d = parseInt(dids)  || 0;

  // Normalize input to a packages dict
  let pkgs;
  if (Array.isArray(faxPackages)) {
    pkgs = getFaxPackages(faxPackages);
  } else if (faxPackages && typeof faxPackages === 'object') {
    pkgs = faxPackages;
  } else {
    pkgs = FAX_PACKAGES;
  }

  // Filter out non-suggestable tiers, sort by users-capacity ASC then
  // dids-capacity ASC. Capacity null/undefined is treated as "unlimited"
  // for sorting purposes (sorts last).
  const candidates = Object.entries(pkgs)
    .filter(([key]) => key !== 'email_only' && key !== 'none')
    .sort(([, a], [, b]) => {
      const aU = a.users == null ? Infinity : a.users;
      const bU = b.users == null ? Infinity : b.users;
      if (aU !== bU) return aU - bU;
      const aD = a.dids == null ? Infinity : a.dids;
      const bD = b.dids == null ? Infinity : b.dids;
      return aD - bD;
    });

  // Capacity null/undefined means "no constraint" — package always fits.
  const fits = (val, cap) => cap == null || val <= cap;

  for (const [key, p] of candidates) {
    if (fits(u, p.users) && fits(d, p.dids)) return key;
  }

  // Nothing fit — fall back to the largest tier (highest capacity)
  return candidates.length > 0 ? candidates[candidates.length - 1][0] : 'infinity';
}

// BYOH — bring your own handset — supported 3CX-compatible devices
export const BYOH_NOTE = 'Client-owned devices must be supported by 3CX. Ferrum will wipe and register each device at the applicable per-handset fee.';

// ─── VOICE PRICING ENGINE ─────────────────────────────────────────────────────
// `faxPackages` is optional — pass DB rows from voice_fax_packages for accurate cost.
// When omitted, falls back to the FAX_PACKAGES constant ($0 cost = unknown margin).
// `catalog` is required for hardware pricing (DB-backed since v3.5.23) — pass the
// result of loadVoiceHardwareCatalog() from the calling page. When omitted,
// hardware lookups silently price at $0 since no catalog is available.
// v3.5.77: calcVoice signature extended with optional cxTiersDB (5th arg).
// When the caller has loaded voice_cx_tiers from the DB, it's passed here
// and forwarded into getCXTiers below. When not provided, the engine falls
// back to the CX_TIERS_BASE constant — same behavior as pre-v3.5.77.
export function calcVoice(v, settings, faxPackages, catalog, cxTiersDB) {
  const yealinkCatalog = catalog?.yealink || [];
  const s = settings || {};
  const taxRate  = parseFloat(s.voice_tax_estimate || 0.25);
  const hosting  = parseFloat(s.voice_hosting_cost || 24);   // AWS Lightsail base
  const supportBundle = parseFloat(s.voice_support_bundle || 295); // hosting + support monthly
  const sipRate  = parseFloat(s.sip_channel_rate || 19.95);
  const didRate  = parseFloat(s.voice_did_rate || 1.50);
  const tfRate   = parseFloat(s.voice_tollfree_rate || 5.00);
  const smsAddOn = parseFloat(s.voice_sms_did_addon || 1.25);
  const e911Rate = parseFloat(s.voice_e911_rate || 2.50);
  const portFee  = parseFloat(s.voice_port_fee || 25.00);
  const smsReg   = parseFloat(s.voice_sms_reg_fee || 65.00);
  const smsCamp  = parseFloat(s.voice_sms_campaign_fee || 15.00);
  const progFee  = parseFloat(s.voice_programming_fee || 25.00);

  let mrr = 0, nrc = 0, costMrr = 0;
  // v3.5.92.2: NRC subcategory counters — per §4a "Programming & Configuration
  // only" decision (May 10 2026), the OnboardingIncentive picker discounts ONLY
  // the labor portion of NRC. These four buckets partition `nrc` so the picker,
  // QuotesPage NRC column, proposal renderer, and Stripe prepayment flow can all
  // operate on the right base without re-running line-filter logic at the
  // consumer site. Identity: nrc === onboardingNrc + hardwareNrc + portingNrc +
  // activationNrc (verified at the end of this function).
  //
  //   onboardingNrc — Programming & Configuration only (the labor-discount base)
  //   hardwareNrc   — phone purchases, BYOH wipe-and-register, lease prepay
  //   portingNrc    — Number Porting per-DID carrier fees
  //   activationNrc — license annual upfronts (hybrid section) + 10DLC brand
  //                   registration (sms section) — regulatory/vendor pass-through
  let onboardingNrc = 0, hardwareNrc = 0, portingNrc = 0, activationNrc = 0;
  const lines = [];

  // ── HOSTED SEATS ────────────────────────────────────────────────────────────
  if (v.quoteType === 'hosted') {
    const seats     = v.seats || 0;
    const seatPrice = parseFloat(v.seatPrice || 0);
    const seatMRR   = seats * seatPrice;

    // 3CX license cost — amortized monthly from annual cost for appropriate tier
    const cxTiers   = getCXTiers(s, cxTiersDB);
    const licType   = v.licenseType || 'pro';
    const cxTier    = cxTiers.find(t => t.type === licType && seats <= t.ext_max)
                   || cxTiers.filter(t => t.type === licType).slice(-1)[0];
    const cxMonthly = cxTier ? cxTier.annual_cost / 12 : 0;

    // Lightsail hosting cost (instance scaled by seat count)
    const lsCost    = getLightsailCost(seats, s);

    // Total Ferrum cost for hosted voice
    const hostedCostMRR = cxMonthly + lsCost;
    const seatCostMRR   = hostedCostMRR; // total cost to deliver hosted voice

    if (seatMRR > 0) {
      lines.push({ label: `${licType === 'enterprise' ? '3CX Enterprise' : '3CX Pro'} Hosted Seats (${seats} × $${seatPrice})`, mrr: seatMRR, cost: seatCostMRR, section: 'seats' });
      mrr += seatMRR; costMrr += seatCostMRR;
    }

    // Show cost breakdown as sub-lines (cost only, no MRR)
    if (cxTier) {
      lines.push({ label: `↳ ${cxTier.label} License ($${cxTier.annual_cost}/yr ÷ 12)`, mrr: 0, cost: cxMonthly, section: 'seats', costOnly: true,
        note: `$${cxMonthly.toFixed(2)}/mo amortized · renews annually` });
    }
    lines.push({ label: `↳ ${getLightsailLabel(seats, s)}`, mrr: 0, cost: lsCost, section: 'seats', costOnly: true });

    // Free devices — $0 line items for transparency
    const freeDevices = [
      v.commonAreaPhones > 0 && { label: `Common Area Phone${v.commonAreaPhones > 1 ? 's' : ''} (${v.commonAreaPhones} included)`, mrr: 0, cost: 0, section: 'devices', note: 'Included' },
      v.voicemailOnly   > 0 && { label: `Voicemail-Only Extensions (${v.voicemailOnly})`,   mrr: 0, cost: 0, section: 'devices', note: 'No charge' },
      v.doorPhones      > 0 && { label: `Door Phones (${v.doorPhones})`,                     mrr: 0, cost: 0, section: 'devices', note: 'No charge' },
      v.pagingDevices   > 0 && { label: `Paging Devices (${v.pagingDevices})`,               mrr: 0, cost: 0, section: 'devices', note: 'No charge' },
      v.specialRingers  > 0 && { label: `Special Ringers (${v.specialRingers})`,             mrr: 0, cost: 0, section: 'devices', note: 'No charge' },
    ].filter(Boolean);
    lines.push(...freeDevices);

    // Crossover analysis
    const hybridCost = calcHybridMRR(seats, v.licenseType || 'pro', v.isManagedIT, supportBundle, hosting, sipRate, v.sipChannels || Math.ceil(seats * 0.3), s, cxTiersDB);
    const crossoverSavings = seatMRR - hybridCost.totalMRR;

  }

  // ── HYBRID HOSTING ───────────────────────────────────────────────────────────
  if (v.quoteType === 'hybrid') {
    // v3.5.77: prefer DB rows (cxTiersDB) when loaded; fall back to the
    // CX_TIERS constant only when DB rows aren't available. Same precedence
    // as getCXTiers above.
    const tierSource = (Array.isArray(cxTiersDB) && cxTiersDB.length > 0) ? cxTiersDB : CX_TIERS;
    const tier = tierSource.find(t => t.id === v.cxTierId);
    if (tier) {
      const monthlyLicense = tier.annual_cost / 12;
      const licenseMRR = v.clientPaysMonthly ? monthlyLicense : 0;
      const licenseNRC = v.clientPaysMonthly ? 0 : tier.annual_cost;
      if (v.clientPaysMonthly) {
        lines.push({ label: `${tier.label} License (amortized monthly)`, mrr: licenseMRR, cost: tier.annual_cost / 12, section: 'hybrid' });
        mrr += licenseMRR; costMrr += tier.annual_cost / 12;
      } else {
        lines.push({ label: `${tier.label} License (annual upfront)`, mrr: 0, nrc: tier.annual_cost, cost: tier.annual_cost, section: 'hybrid' });
        nrc += tier.annual_cost;
        activationNrc += tier.annual_cost;  // v3.5.92.2 §4a — license upfront is regulatory/vendor pass-through, not labor
      }
    }

    // Hosting + support bundle
    if (!v.isManagedIT) {
      lines.push({ label: 'Hosted PBX — Hosting & Support', mrr: supportBundle, cost: hosting, section: 'hybrid' });
      mrr += supportBundle; costMrr += hosting;
    } else {
      lines.push({ label: 'Hosted PBX — Hosting Only (Managed IT client)', mrr: parseFloat(s.voice_hosting_sell || 105), cost: hosting, section: 'hybrid' });
      mrr += parseFloat(s.voice_hosting_sell || 105); costMrr += hosting;
    }

    // Larger instance
    if (v.largerInstance) {
      const instanceUpgrade = parseFloat(s.voice_instance_upgrade || 48);
      lines.push({ label: 'Enhanced VM Instance (API/reporting integrations)', mrr: instanceUpgrade, cost: instanceUpgrade * 0.8, section: 'hybrid' });
      mrr += instanceUpgrade; costMrr += instanceUpgrade * 0.8;
    }
  }

  // ── SIP TRUNKING (both hosted and SIP-only) ───────────────────────────────
  const channels = parseInt(v.sipChannels || 0);
  if (channels > 0) {
    const sipMRR  = channels * sipRate;
    const sipCost = channels * parseFloat(s.sip_channel_cost || 8.00);
    lines.push({ label: `SIP Trunking (${channels} concurrent paths × $${sipRate})`, mrr: sipMRR, cost: sipCost, section: 'sip' });
    mrr += sipMRR; costMrr += sipCost;
  }

  // ── NUMBERS & DIDs ─────────────────────────────────────────────────────────
  const localDIDs = parseInt(v.localDIDs || 0);
  if (localDIDs > 0) {
    const didMRR = localDIDs * didRate;
    lines.push({ label: `Local DID Numbers w/ CNAM (${localDIDs} × $${didRate})`, mrr: didMRR, cost: localDIDs * 0.50, section: 'numbers' });
    mrr += didMRR; costMrr += localDIDs * 0.50;
  }
  const smsDIDs = parseInt(v.smsDIDs || 0);
  if (smsDIDs > 0) {
    const smsDIDMRR = smsDIDs * smsAddOn;
    lines.push({ label: `SMS/MMS Enabled DIDs (${smsDIDs} × $${smsAddOn} add-on)`, mrr: smsDIDMRR, cost: smsDIDs * 0.40, section: 'numbers' });
    mrr += smsDIDMRR; costMrr += smsDIDs * 0.40;
  }
  const tfNumbers = parseInt(v.tollFreeNumbers || 0);
  if (tfNumbers > 0) {
    const tfMRR = tfNumbers * tfRate;
    lines.push({ label: `Toll-Free Numbers (${tfNumbers} × $${tfRate})`, mrr: tfMRR, cost: tfNumbers * 1.50, section: 'numbers' });
    mrr += tfMRR; costMrr += tfNumbers * 1.50;
  }
  if (v.tollFreePerMin) {
    const tfpm = Math.max(0.019, parseFloat(v.tollFreePerMinRate ?? 0.05));
    lines.push({ label: `Toll-Free Usage — $${tfpm.toFixed(3)}/min (metered)`, mrr: 0, cost: 0, section: 'numbers', note: `$${tfpm.toFixed(3)}/min — billed monthly in arrears`, metered: true });
  }
  const e911DIDs = parseInt(v.e911DIDs || 0);
  if (e911DIDs > 0) {
    const e911MRR = e911DIDs * e911Rate;
    lines.push({ label: `E911 Enabled DIDs (${e911DIDs} × $${e911Rate})`, mrr: e911MRR, cost: e911DIDs * 1.00, section: 'numbers' });
    mrr += e911MRR; costMrr += e911DIDs * 1.00;
  }

  // ── FAX ───────────────────────────────────────────────────────────────────
  // ── Virtual Fax Package ───────────────────────────────────────────────────
  if (v.faxType && v.faxType !== 'none') {
    const faxCatalog = getFaxPackages(faxPackages);
    const fp = faxCatalog[v.faxType];
    if (fp) {
      const faxUsers   = parseInt(v.faxUsers || 1);
      const faxDIDs    = parseInt(v.faxDIDs  || 1);
      let faxMRR  = fp.price;
      let faxCost = fp.cost || 0;

      // Extra users/DIDs beyond base package — sell + cost tracked separately
      const extraUserCount = fp.extra_user && faxUsers > fp.users ? (faxUsers - fp.users) : 0;
      const extraDIDCount  = fp.extra_did  && faxDIDs  > fp.dids  ? (faxDIDs  - fp.dids)  : 0;
      const extraUsersSell = extraUserCount * (fp.extra_user || 0);
      const extraDIDsSell  = extraDIDCount  * (fp.extra_did  || 0);
      const extraUsersCost = extraUserCount * (fp.extra_user_cost || 0);
      const extraDIDsCost  = extraDIDCount  * (fp.extra_did_cost  || 0);
      faxMRR  += extraUsersSell + extraDIDsSell;
      faxCost += extraUsersCost + extraDIDsCost;

      lines.push({ label: fp.label, mrr: fp.price, cost: fp.cost || 0, section: 'fax', desc: fp.desc });
      mrr += fp.price; costMrr += (fp.cost || 0);
      if (extraUsersSell > 0) {
        lines.push({ label: `Fax Extra Users (${extraUserCount} × $${fp.extra_user})`, mrr: extraUsersSell, cost: extraUsersCost, section: 'fax' });
        mrr += extraUsersSell; costMrr += extraUsersCost;
      }
      if (extraDIDsSell > 0) {
        lines.push({ label: `Fax Extra DIDs (${extraDIDCount} × $${fp.extra_did})`, mrr: extraDIDsSell, cost: extraDIDsCost, section: 'fax' });
        mrr += extraDIDsSell; costMrr += extraDIDsCost;
      }
      if (fp.overage_page) lines.push({ label: 'Fax Overage Rate', mrr: 0, cost: 0, section: 'fax', note: `$${fp.overage_page}/page over ${fp.pages} pages`, metered: true });

      if (false) { // placeholder so old ATA block closes cleanly
        nrc += 0;
      }
    }
  }

  // ── CALL RECORDING ────────────────────────────────────────────────────────
  if (v.callRecording) {
    const crBase    = parseFloat(s.voice_call_recording_rate || 35);
    const crExtRate = parseFloat(s.voice_call_recording_ext_rate || 15);
    const crDays    = parseInt(v.callRecordingDays || 30);
    const extraMonths = Math.max(0, Math.round((crDays - 30) / 30));
    const crMRR = crBase + extraMonths * crExtRate;
    lines.push({ label: `Call Recording — ${crDays}-day retention`, mrr: crMRR, cost: crMRR * 0.5, section: 'addons',
      desc: crDays === 30 ? '30 days included' : `30 days base + ${extraMonths} extra month${extraMonths > 1 ? 's' : ''} storage` });
    mrr += crMRR; costMrr += crMRR * 0.5;
  }

  // ── SMS/MMS — required when smsDIDs > 0 ─────────────────────────────────
  const smsRequired = smsDIDs > 0;
  if (v.smsEnabled || smsRequired) {
    if (v.smsNewRegistration !== false) {
      lines.push({ label: '10DLC Brand Registration (one-time)', mrr: 0, nrc: smsReg, cost: smsReg, section: 'sms' });
      nrc += smsReg;
      activationNrc += smsReg;  // v3.5.92.2 §4a — regulatory pass-through, not labor
    }
    // Support smsCampaignList array (new) or legacy smsCampaigns count
    const campaignList = v.smsCampaignList || [];
    const numCampaigns = campaignList.length > 0 ? campaignList.length : parseInt(v.smsCampaigns || 1);
    if (numCampaigns > 0) {
      const campMRR = numCampaigns * smsCamp;
      lines.push({ label: `SMS Campaigns (${numCampaigns} × $${smsCamp}/mo)`, mrr: campMRR, cost: campMRR * 0.7, section: 'sms' });
      mrr += campMRR; costMrr += campMRR * 0.7;
    }
    lines.push({ label: 'SMS — $0.02/segment · MMS — $0.04/segment', mrr: 0, cost: 0, section: 'sms', note: 'Metered — billed monthly in arrears.', metered: true });
  }

  // ── HARDWARE — supports mixed models ────────────────────────────────────
  const hwItems = v.hardwareItems || (v.hardwareModel && v.hardwareQty > 0 ? [{ model: v.hardwareModel, qty: v.hardwareQty }] : []);
  if (v.hardwareType && v.hardwareType !== 'none' && hwItems.length > 0) {
    for (const item of hwItems) {
      const model = yealinkCatalog.find(m => m.id === item.model);
      const qty   = parseInt(item.qty || 0);
      if (!model || qty <= 0) continue;

      if (v.hardwareType === 'lease') {
        const leaseMRR = qty * model.monthly;
        lines.push({ label: `${model.label} — Evergreen Lease (${qty} × $${model.monthly}/mo)`, mrr: leaseMRR, cost: leaseMRR * 0.6, section: 'hardware' });
        mrr += leaseMRR; costMrr += leaseMRR * 0.6;
      } else {
        // v3.5.86.5: hardware tier discount thresholds + percentage moved
        // from hardcoded magic numbers (36 / 24 / 0.5) to pricing_settings
        // rows. Defaults preserve prior behavior: free at 36mo, optional
        // 50% off at 24mo via the hardwareDiscount50 toggle. See SQL
        // migration 2026_05_08_voice_hardware_tiers.sql for the seeded
        // defaults; admin can edit in Pricing Settings tab.
        const hwFreeTerm = parseInt(s.voice_hardware_free_term, 10);
        const hwDiscTerm = parseInt(s.voice_hardware_disc_term, 10);
        const hwDiscPct  = parseFloat(s.voice_hardware_disc_pct);
        const freeTerm   = Number.isFinite(hwFreeTerm) ? hwFreeTerm : 36;
        const discTerm   = Number.isFinite(hwDiscTerm) ? hwDiscTerm : 24;
        const discPct    = Number.isFinite(hwDiscPct)  ? hwDiscPct  : 0.5;

        let unitPrice = model.nrc;
        let discountNote = '';
        if (v.contractTerm === freeTerm) {
          unitPrice = 0;
          discountNote = ` — free with ${freeTerm}-month contract`;
        } else if (v.contractTerm === discTerm && v.hardwareDiscount50) {
          unitPrice = model.nrc * (1 - discPct);
          discountNote = ` — ${Math.round(discPct * 100)}% off with ${discTerm}-month contract`;
        }
        const hwNRC = qty * unitPrice;
        lines.push({ label: `${model.label} — Purchase (${qty} × $${unitPrice}${discountNote})`, mrr: 0, nrc: hwNRC, cost: qty * model.nrc * 0.7, section: 'hardware' });
        nrc += hwNRC;
        hardwareNrc += hwNRC;  // v3.5.92.2 §4a
      }
    }
    // Shipping note — billed at end of implementation at current UPS rate
    if (v.hardwareType === 'purchase' && hwItems.reduce((s,i) => s + parseInt(i.qty||0),0) > 0) {
      lines.push({ label: 'Shipping — billed at end of implementation at then-current UPS rates', mrr: 0, nrc: 0, cost: 0, section: 'hardware', note: 'TBD' });
    }
  }

  // ── BYOH — bring your own handset ────────────────────────────────────────
  const byohItems = v.byohItems || [];
  const byohFee   = parseFloat(s.voice_byoh_fee || 20);
  if (byohItems.length > 0) {
    const totalByoh = byohItems.reduce((sum, i) => sum + parseInt(i.qty||0), 0);
    if (totalByoh > 0) {
      const byohNRC = totalByoh * byohFee;
      for (const item of byohItems) {
        const qty = parseInt(item.qty||0);
        if (qty <= 0) continue;
        lines.push({ label: `BYOH — ${item.model || 'Client device'} (${qty} × wipe & register)`, mrr: 0, nrc: qty * byohFee, cost: qty * byohFee * 0.3, section: 'hardware' });
        nrc += qty * byohFee;
        hardwareNrc += qty * byohFee;  // v3.5.92.2 §4a — wipe-and-register is a hardware-handling fee, not Programming labor
      }
    }
  }

  // ── PROGRAMMING FEE ────────────────────────────────────────────────────────
  const programmableSeats = v.seats || 0;
  if (programmableSeats > 0 && !v.waiveProgrammingFee) {
    const pf = programmableSeats * progFee;
    lines.push({ label: `Programming & Configuration (${programmableSeats} seats × $${progFee})`, mrr: 0, nrc: pf, cost: pf * 0.4, section: 'onetime' });
    nrc += pf;
    onboardingNrc += pf;  // v3.5.92.2 §4a — THE discount-eligible labor bucket (Programming & Configuration ONLY)
  }

  // ── NUMBER PORTING ─────────────────────────────────────────────────────────
  const portQty = parseInt(v.portingNumbers || 0);
  if (portQty > 0) {
    const portNRC = portQty * portFee;
    lines.push({ label: `Number Porting (${portQty} × $${portFee})`, mrr: 0, nrc: portNRC, cost: portNRC * 0.2, section: 'onetime' });
    nrc += portNRC;
    portingNrc += portNRC;  // v3.5.92.2 §4a — per-DID carrier fees, NOT Programming labor
  }

  // ── BUNDLE DISCOUNT ────────────────────────────────────────────────────────
  let bundleDiscount = 0;
  if (v.isManagedIT && mrr > 0) {
    const discRate = parseFloat(s.voice_bundle_discount || 0.10);
    bundleDiscount = mrr * discRate;
    if (bundleDiscount > 0) {
      lines.push({ label: `Bundle Discount — Managed IT Client (${Math.round(discRate * 100)}% off voice MRR)`, mrr: -bundleDiscount, cost: 0, section: 'discount' });
    }
  }

  const finalMRR  = mrr - bundleDiscount;
  const totalCost = costMrr;
  const gm        = finalMRR > 0 ? 1 - totalCost / finalMRR : 0;
  const estTax    = finalMRR * taxRate;

  // v3.5.92.2: identity check — nrc must equal the sum of its four buckets.
  // Catches any future NRC addition site that forgets to bucket. Throws in
  // dev only (NODE_ENV != 'production') so production builds skip the cost
  // even though the math is fast — keeps calcVoice strictly free of any
  // dev-only assertions on the hot path. Off-by-one cent from rounding is
  // tolerated via a 0.01 epsilon.
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
    const sum = onboardingNrc + hardwareNrc + portingNrc + activationNrc;
    if (Math.abs(sum - nrc) > 0.01) {
      // eslint-disable-next-line no-console
      console.warn(`[calcVoice] NRC bucket identity violated: nrc=${nrc} but onboarding+hardware+porting+activation=${sum}. New NRC addition site missing a bucket increment?`);
    }
  }

  return { lines, mrr, bundleDiscount, finalMRR, nrc, costMrr, totalCost, gm, estTax, taxRate,
    // v3.5.92.2: NRC subcategory totals — see counter declarations above.
    // Identity: nrc === onboardingNrc + hardwareNrc + portingNrc + activationNrc.
    onboardingNrc, hardwareNrc, portingNrc, activationNrc };
}

// Calc what hybrid would cost for crossover comparison
// v3.5.77: accepts optional cxTiersDB as last argument; forwards to
// getRecommendedTier so the crossover calc uses the same DB tier data
// the main calcVoice path uses. Existing callers without cxTiersDB get
// the CX_TIERS_BASE fallback (preserves pre-v3.5.77 behavior).
export function calcHybridMRR(seats, licenseType, isManagedIT, supportBundle, hosting, sipRate, channels, s, cxTiersDB) {
  const tier      = getRecommendedTier(seats, licenseType, s, cxTiersDB);
  const licMonthly = tier.annual_cost / 12;
  const hostSell   = isManagedIT ? parseFloat(s?.voice_hosting_sell || 105) : supportBundle;
  const sipMRR     = channels * sipRate;
  return { totalMRR: licMonthly + hostSell + sipMRR, tier, licMonthly, hostSell, sipMRR };
}

// ─── BUNDLE DISCOUNT LOGIC ────────────────────────────────────────────────────
// contractTerm: 12 | 24 | 36
// itBaseMRR: wB+uB+sB+lB+tB+vB only — no add-ons, no uplifts
// voiceMRR: before discount
//
// v3.5.86.2.2: tier rates and free-phone thresholds were hardcoded prior to
// this deploy. Now read from pricing_settings:
//   bundle_voice_disc_${term}        — decimal fraction discount per term
//   bundle_free_phones_min_term      — minimum contract term for free phones
//   bundle_free_phones_min_it_mrr    — minimum IT base MRR for free phones
// Defaults preserve prior behavior verbatim (5/10/15% at 12/24/36 mo, free
// phones at 36 mo + $750 IT MRR floor) — see the v3.5.86.2.2 SQL migration
// that seeds the rows.
//
// `settings` is optional for backwards compatibility — callers that haven't
// been updated yet still work with hardcoded defaults. The companion code
// deploy updates BundleQuotePage's call site to pass settings. Once every
// caller is updated, the optional fallback can be tightened to required.
export function calcBundleDiscount(contractTerm, itBaseMRR, voiceMRR, settings) {
  const s = settings || {};
  const rate = parseFloat(s[`bundle_voice_disc_${contractTerm}`]);
  const tierRate = Number.isFinite(rate) ? rate : ({ 12: 0.05, 24: 0.10, 36: 0.15 }[contractTerm] || 0);
  const minTerm = parseInt(s.bundle_free_phones_min_term, 10);
  const minItMrr = parseFloat(s.bundle_free_phones_min_it_mrr);
  const minTermResolved = Number.isFinite(minTerm) ? minTerm : 36;
  const minItMrrResolved = Number.isFinite(minItMrr) ? minItMrr : 750;
  const freePhones = contractTerm >= minTermResolved && itBaseMRR >= minItMrrResolved;
  const voiceDiscount = voiceMRR * tierRate;
  return { rate: tierRate, voiceDiscount, freePhones, qualifies: tierRate > 0 };
}

// ─── BUNDLE CONTRACT TERM CARDS HELPER (v3.5.86.2.2) ──────────────────────────
// Returns the [term, label, sublabel] tuples that BundleQuotePage's contract
// term card grid uses. Mirrors the shape of the prior hardcoded array:
//   [[12,'12 mo','5% voice disc.'],[24,'24 mo','10% voice disc.'],[36,'36 mo','15% + free phones']]
//
// Sublabel content:
//   - "${pct}% voice disc." when bundle_voice_disc_${term} > 0
//   - " + free phones" suffix when term === bundle_free_phones_min_term
//   - empty string when neither condition fires (e.g. an admin zeroes out
//     the discount and the term isn't the free-phones minimum)
//
// The "+ free phones" suffix is informational — it tells the rep "free
// phones come with this term IF the IT MRR threshold is met." Whether the
// CURRENT quote qualifies is computed by calcBundleDiscount based on the
// quote's actual itBaseMRR; this helper just labels which term is the
// free-phones term in general.
export function getBundleVoiceDiscOptions(settings, terms = [12, 24, 36]) {
  const s = settings || {};
  const minTerm = parseInt(s.bundle_free_phones_min_term, 10);
  const minTermResolved = Number.isFinite(minTerm) ? minTerm : 36;
  return terms.map(term => {
    const rate = parseFloat(s[`bundle_voice_disc_${term}`]) || 0;
    const pct  = Math.round(rate * 100);
    const parts = [];
    if (pct > 0) parts.push(`${pct}% voice disc.`);
    if (term === minTermResolved && pct > 0) {
      // "+ free phones" replaces the trailing period on the disc text for
      // a cleaner read: "15% + free phones" not "15% voice disc. + free phones"
      parts[parts.length - 1] = `${pct}% + free phones`;
    } else if (term === minTermResolved && pct === 0) {
      parts.push('+ free phones');
    }
    return [term, `${term} mo`, parts.join(' ')];
  });
}
